import { useEffect } from "react";
import { useParams } from "react-router-dom";
import Spinner from "../layout/Spinner";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { cancelPayment } from "../../slices/confirmation";
import { translateText } from "../../utils/translation";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/solid";

export default function PaymentCancellation() {
  const dispatch = useAppDispatch();

  // Get string parameters from URL
  const { transaction_id: transactionId, property_id: propertyId } =
    useParams();

  // Send API request to update request
  useEffect(() => {
    if (transactionId === undefined || propertyId === undefined) return;
    dispatch(cancelPayment({ transactionId, propertyId }));
  }, [dispatch, propertyId, transactionId]);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const {
    payment: { cancellation },
  } = useAppSelector((state) => state.confirmation);
  const language = user === null ? "EN" : user.language;

  // Functions
  function renderLoadingContent() {
    return (
      <div className="m-auto grid grid-cols-1">
        <Spinner className="mx-auto" />
        <p className="text-center text-gray-500 pt-5 px-16 text-sm sm:text-base">
          {translateText("payment_cancellation_loading", language)}
        </p>
      </div>
    );
  }

  function renderFailedContent() {
    return (
      <div className="m-auto bg-danger px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16 grid grid-cols-1">
        <ExclamationTriangleIcon
          className="animate-bounce mx-auto h-12 w-12 text-white"
          aria-hidden="true"
        />
        <p className="text-center text-white pt-5 px-16 text-base sm:text-xl">
          {translateText("payment_cancellation_failed", language)}
        </p>
      </div>
    );
  }

  function renderSuccessContent() {
    return (
      <div className="m-auto bg-success px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16 grid grid-cols-1">
        <CheckCircleIcon
          className="animate-bounce mx-auto h-12 w-12 text-white"
          aria-hidden="true"
        />
        <p className="text-center text-white pt-5 px-16 text-base sm:text-xl">
          {translateText("payment_cancellation_success", language)}
        </p>
      </div>
    );
  }

  function renderExpiredContent() {
    return (
      <div className="m-auto bg-warning px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16 grid grid-cols-1">
        <ExclamationCircleIcon
          className="animate-bounce mx-auto h-12 w-12 text-black"
          aria-hidden="true"
        />
        <p className="text-center text-black pt-5 px-16 text-base sm:text-xl">
          {translateText("payment_cancellation_expired", language)}
        </p>
      </div>
    );
  }

  function renderNullContent() {
    return (
      <div className="m-auto bg-warning px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16 grid grid-cols-1">
        <QuestionMarkCircleIcon
          className="animate-bounce mx-auto h-12 w-12 text-black"
          aria-hidden="true"
        />
        <p className="text-center text-black pt-5 px-16 text-base sm:text-xl">
          {translateText("payment_cancellation_null", language)}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-screen mx-auto max-w-7xl py-24 sm:px-6 sm:py-32 lg:px-8">
      {transactionId === undefined
        ? renderNullContent()
        : !Object.keys(cancellation).includes(transactionId)
        ? renderLoadingContent()
        : cancellation[transactionId].loading
        ? renderLoadingContent()
        : cancellation[transactionId].status === 200
        ? renderSuccessContent()
        : cancellation[transactionId].status === 400
        ? renderExpiredContent()
        : renderFailedContent()}
    </div>
  );
}
